import { cn, getObjectKeys } from "@gettactic/helpers";
import { capitalize } from "@gettactic/helpers/src/strings/capitalize";
import { Search, Upload } from "lucide-react";
import React from "react";
import Scrollbars, { positionValues } from "react-custom-scrollbars";
import { useIconDictionaries } from "../../icons/LucideIcon/useIconDictionaries";
import { Tabs, TabsList, TabsTrigger } from "../../primitives";
import { IconPickerContext, IconPickerProvider } from "./IconPicker.provider";
import {
  IconDictionaries,
  IconDictionaryItem,
  IconPickerProps
} from "./IconPicker.types";
import { IconPickerIconButton } from "./IconPickerIconButton";

const classes = {
  IconPickerWrapper: "h-[320px]",
  IconPickerLoadingWrapper: "items-center justify-center",
  IconPickerGrid: "gap-2 p-3 grid-cols-[1fr_1fr_1fr_1fr_1fr]",
  Tabs: "w-full h-full flex flex-col",
  TabsList: "py-2 px-2 rounded-none border-b bg-muted",
  SearchWrapper:
    "flex items-center gap-2 border-b px-3 py-2",
  SearchInput:
    "flex-1 bg-transparent text-sm outline-none placeholder:text-neutral-400"
};

export const IconPicker: React.FC<IconPickerProps> = (props) => {
  const iconDictionaries = useIconDictionaries();

  if (!iconDictionaries) {
    return (
      <div
        className={cn(
          "flex",
          classes.IconPickerWrapper,
          classes.IconPickerLoadingWrapper
        )}
      >
        Loading...
      </div>
    );
  }

  return <IconPickerInner iconDictionaries={iconDictionaries} {...props} />;
};

export const IconPickerInner: React.FC<
  IconPickerProps & { iconDictionaries: IconDictionaries }
> = ({ onSelect, initialIconSet, iconDictionaries, customSets, onUploadIcon }) => {
  const scrollBarsRef = React.useRef<Scrollbars | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [uploadName, setUploadName] = React.useState("");
  const [uploadSvg, setUploadSvg] = React.useState("");
  const [uploadFileName, setUploadFileName] = React.useState("");
  const [isUploading, setIsUploading] = React.useState(false);
  const [showUploadForm, setShowUploadForm] = React.useState(false);

  const handleIconSelect = (iconName: string, iconSet: string) => {
    onSelect?.({ icon: iconName, set: iconSet });
  };

  const handleSearchValueChange = () => {
    scrollBarsRef.current?.scrollTop(0);
  };

  const iconPickerDictionaries: Record<string, IconDictionaryItem> =
    React.useMemo(() => {
      return {
        ...iconDictionaries,
        custom: {
          icons: [] as never,
          keys: [] as never
        },
        ...customSets
      };
    }, [iconDictionaries, customSets]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadFileName(file.name);
    if (!uploadName) {
      setUploadName(file.name.replace(/\.svg$/i, ""));
    }
    const reader = new FileReader();
    reader.onload = () => {
      setUploadSvg(reader.result as string);
    };
    reader.readAsText(file);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleUploadSubmit = async () => {
    if (!uploadName.trim() || !uploadSvg.trim() || !onUploadIcon) return;
    setIsUploading(true);
    try {
      await onUploadIcon(uploadName.trim(), uploadSvg.trim());
      setUploadName("");
      setUploadSvg("");
      setUploadFileName("");
      setShowUploadForm(false);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <IconPickerProvider
      dictionaries={iconPickerDictionaries}
      onSearchValueChange={handleSearchValueChange}
      initialIconSet={initialIconSet}
    >
      <IconPickerContext.Consumer>
        {(state) => {
          if (!state) return null;

          const handleScrollChange = (values: positionValues) => {
            if (values.top >= 0.9 && state.page + 1 <= state.totalIconsPages) {
              state.setPage((page) => page + 1);
            }
          };

          return (
            <>
              <Tabs
                defaultValue="custom"
                value={state.iconSet}
                className={classes.Tabs}
                onValueChange={(value) => state.setIconSet(value as never)}
              >
                <TabsList className={classes.TabsList}>
                  {customSets &&
                    getObjectKeys(customSets).map((key) => (
                      <TabsTrigger key={key} value={key}>
                        {capitalize(key)}
                      </TabsTrigger>
                    ))}
                  <TabsTrigger value="mdi">Material Icons</TabsTrigger>
                  <TabsTrigger value="lucide">Lucide Icons</TabsTrigger>
                </TabsList>
              </Tabs>
              <div className={classes.SearchWrapper}>
                <Search size={16} className="shrink-0 text-neutral-400" />
                <input
                  className={classes.SearchInput}
                  onChange={(e) => {
                    state.setSearchValue(e.target.value);
                  }}
                  placeholder="Search icons..."
                />
                {onUploadIcon && (
                  <button
                    type="button"
                    className="shrink-0 rounded p-1 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700"
                    onClick={() => setShowUploadForm((v) => !v)}
                    title="Upload custom icon"
                  >
                    <Upload size={16} />
                  </button>
                )}
              </div>
              {showUploadForm && onUploadIcon && (
                <div className="flex flex-col gap-2 border-b px-3 py-3 bg-neutral-50">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".svg"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={uploadName}
                      onChange={(e) => setUploadName(e.target.value)}
                      placeholder="Icon name"
                      className="flex-1 rounded border border-neutral-300 px-2 py-1.5 text-sm outline-none focus:border-neutral-500"
                    />
                    <button
                      type="button"
                      className="shrink-0 rounded border border-neutral-300 bg-white px-3 py-1.5 text-sm hover:bg-neutral-50"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {uploadFileName || "Choose SVG"}
                    </button>
                  </div>
                  {uploadSvg && (
                    <div className="flex h-12 w-12 items-center justify-center rounded border border-neutral-200 bg-white p-1">
                      <div
                        className="h-full w-full [&>svg]:h-full [&>svg]:w-full"
                        // biome-ignore lint/security/noDangerouslySetInnerHtml: Preview of user-selected SVG file
                        dangerouslySetInnerHTML={{ __html: uploadSvg }}
                      />
                    </div>
                  )}
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      className="rounded px-3 py-1.5 text-sm text-neutral-600 hover:bg-neutral-100"
                      onClick={() => {
                        setShowUploadForm(false);
                        setUploadName("");
                        setUploadSvg("");
                        setUploadFileName("");
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="rounded bg-neutral-900 px-3 py-1.5 text-sm text-white hover:bg-neutral-800 disabled:opacity-50"
                      onClick={handleUploadSubmit}
                      disabled={
                        !uploadName.trim() || !uploadSvg.trim() || isUploading
                      }
                    >
                      {isUploading ? "Uploading..." : "Upload"}
                    </button>
                  </div>
                </div>
              )}
              <Scrollbars
                ref={scrollBarsRef}
                className={classes.IconPickerWrapper}
                onUpdate={handleScrollChange}
                style={{ height: undefined }}
              >
                <div className={cn("grid", classes.IconPickerGrid)}>
                  {state.availableIcons
                    ? state.availableIcons.map((iconName) => {
                        return (
                          <div key={iconName}>
                            <IconPickerIconButton
                              iconName={
                                iconPickerDictionaries[state.iconSet].icons[
                                  iconName
                                ]
                              }
                              onClick={handleIconSelect.bind(
                                null,
                                iconName,
                                state.iconSet
                              )}
                              icon={iconName}
                              iconSet={state.iconSet}
                              iconUrl={iconPickerDictionaries[state.iconSet].urls?.[iconName]}
                            />
                          </div>
                        );
                      })
                    : undefined}
                </div>
              </Scrollbars>
            </>
          );
        }}
      </IconPickerContext.Consumer>
    </IconPickerProvider>
  );
};
